// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, MapPin, Info, ArrowRight } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface Event {
  id: string;
  slug: string;
  name: string;
  description?: string;
  start_date: string;
  end_date?: string;
  status: string;
  thumbnail_url?: string;
  gender?: string;
  sport_id?: string;
  sport?: { id: string; name: string }[];
  venue?: {
    name: string;
    city: string;
    state: string;
  } | null;
  event_category_mappings: Array<{
    event_categories: {
      id: string;
      name: string;
    };
  }>;
}

const DemoEvents: React.FC = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDemoEvents = async () => {
    try {
      console.log("🔄 Fetching demo events...");
      
      const { data, error } = await supabase
        .from('events')
        .select(`
          id,
          slug,
          name,
          description,
          start_date,
          end_date,
          status,
          thumbnail_url,
          gender,
          sport_id,
          venue:venues!events_main_venue_id_fkey(
            name,
            city,
            state
          ),
          event_category_mappings!event_category_mappings_event_id_fkey(
            event_categories!event_category_mappings_event_category_id_fkey(id, name)
          )
        `)
        .eq('is_demo', true)
        .eq('is_active', true)
        .order('start_date', { ascending: true });

      if (error) throw error;

      console.log("✅ Fetched demo events:", data?.length || 0);
      
      // Transform the data to match our Event interface
      const transformedEvents = (data || []).map(event => ({
        ...event,
        venue: event.venue?.[0] || null,
        event_category_mappings: event.event_category_mappings.map((mapping: any) => ({
          event_categories: mapping.event_categories[0] || { id: '', name: '' }
        }))
      }));
      
      setEvents(transformedEvents);
    } catch (err: any) {
      console.error("❌ Error fetching demo events:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDemoEvents();
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleEventClick = (eventSlug: string) => {
    navigate(`/events/${eventSlug}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background py-12">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading demo events...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Demo Banner */}
        <Alert className="mb-8 border-[#BC8D09] bg-gradient-to-r from-[#BC8D09]/10 to-[#BC8D09]/5">
          <Info className="h-5 w-5 text-[#BC8D09]" />
          <AlertDescription className="text-base">
            <span className="font-bold text-[#BC8D09]">DEMO MODE</span> - This is a demonstration of RecruitLook's event management system. These events are for showcase purposes only and demonstrate the platform's capabilities for managing recruiting events, tournaments, and camps.
          </AlertDescription>
        </Alert>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-3">Demo Events</h1>
          <p className="text-lg text-muted-foreground">
            Explore sample events to see how RecruitLook streamlines athletic event management
          </p>
        </div>

        {/* Events Grid */}
        {error ? (
          <Alert variant="destructive">
            <AlertDescription>
              Error loading demo events: {error}
            </AlertDescription>
          </Alert>
        ) : events.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground mb-4">No demo events available at this time.</p>
              <Button onClick={() => navigate('/events')} variant="outline">
                View Live Events
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((event) => (
              <Card 
                key={event.id} 
                className="group hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden border-2 hover:border-[#BC8D09]/30"
                onClick={() => handleEventClick(event.slug)}
              >
                {/* Demo Badge Overlay */}
                <div className="absolute top-3 right-3 z-10">
                  <Badge className="bg-[#BC8D09] text-white shadow-md">DEMO</Badge>
                </div>

                {/* Event Thumbnail */}
                {event.thumbnail_url ? (
                  <div className="relative h-48 overflow-hidden bg-muted">
                    <img
                      src={event.thumbnail_url}
                      alt={event.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                ) : (
                  <div className="h-48 bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                    <Calendar className="h-16 w-16 text-primary/40" />
                  </div>
                )}

                <CardHeader>
                  <div className="space-y-2">
                    <CardTitle className="text-xl group-hover:text-primary transition-colors line-clamp-2">
                      {event.name}
                    </CardTitle>
                    
                    {/* Event Categories */}
                    {event.event_category_mappings.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {event.event_category_mappings.slice(0, 2).map((mapping) => (
                          <Badge key={mapping.event_categories.id} variant="outline" className="text-xs">
                            {mapping.event_categories.name}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="space-y-3">
                  {/* Description */}
                  {event.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {event.description}
                    </p>
                  )}

                  {/* Event Details */}
                  <div className="space-y-2 pt-2 border-t">
                    {/* Date */}
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <span className="text-foreground">
                        {formatDate(event.start_date)}
                        {event.end_date && event.end_date !== event.start_date && 
                          ` - ${formatDate(event.end_date)}`
                        }
                      </span>
                    </div>

                    {/* Venue */}
                    {event.venue && (
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <span className="text-foreground truncate">
                          {event.venue.name && `${event.venue.name}, `}
                          {event.venue.city}, {event.venue.state}
                        </span>
                      </div>
                    )}

                    {/* Gender */}
                    {event.gender && (
                      <div className="flex items-center gap-2 text-sm">
                        <Info className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <span className="text-foreground capitalize">{event.gender}</span>
                      </div>
                    )}
                  </div>

                  {/* View Details Button */}
                  <Button 
                    className="w-full mt-4 group/btn" 
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEventClick(event.slug);
                    }}
                  >
                    View Details
                    <ArrowRight className="h-4 w-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Footer Info */}
        <div className="mt-12 text-center">
          <Card className="bg-muted/30">
            <CardContent className="py-8">
              <h3 className="text-lg font-semibold mb-2">Interested in RecruitLook?</h3>
              <p className="text-muted-foreground mb-4">
                See how our platform can transform your athletic recruiting and event management
              </p>
              <div className="flex gap-3 justify-center flex-wrap">
                <Button onClick={() => navigate('/pricing')}>
                  View Pricing
                </Button>
                <Button onClick={() => navigate('/contact')} variant="outline">
                  Contact Sales
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DemoEvents;